import { wallOpeningBottomZ, wallOpeningPlanExtents } from "building-common/openings/openingQuery";
import { FinishClipping } from "building-types/casing/casingTypes";
import type { DoorDescriptor, GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type { BoardDescriptor, CustomDescriptor } from "building-types/profiles/profileTypes";
import { ProfileExtrusionDescriptorType, ProfileExtrusionType } from "building-types/profiles/profileTypesEnum";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { ExteriorDoorCasingSettings } from "building-types/settings/exteriorDoorCasingSettings";
import type { ExteriorWindowCasingSettings } from "building-types/settings/exteriorWindowCasingSettings";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import {
  ApronCasingType,
  HeadCasingType,
  SideCasingType,
  StoolCasingType,
} from "building-types/settings/settingsTypesEnum";
import type { WallCut } from "building-types/walls/wallCutTypes";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { StartSortedWallGaps, WallGap } from "building-types/walls/wallGap";
import { WallGapSource } from "building-types/walls/wallGap";
import type { SolidWall, SolidWallSide, WallMassBase } from "building-types/walls/wallTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import { FLOOR_PLAN_CUT_PLANE } from "common/constants/buildingObjects";
import type { Table } from "common/data/table";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { levelInchesNew, levelInchesToWorld } from "common/utils/nominalNumbers";
import { WINDOW_INTERIOR_FRAME_WIDTH } from "doc/elements/window/constants";
import {
  defaultExteriorDoorCasingSettings,
  defaultExteriorWindowCasingSettings,
  defaultInteriorDoorCasingSettings,
} from "doc/settings/defaultSettings";
import { isHeadCasingArchedSlices } from "doc/settingsUtils";
import { DOOR_UNIT_TRIM_CROSS_SECTION } from "doc/utils/doorTools";
import { isHeadCasingNone, isHeadCasingSlices } from "doc/utils/headCasingTools";
import { startSortedWallGapsNew, wallGapNew } from "doc/utils/wallGapTools";
import { closestParamNormalizedLine2 } from "geom/closestPoint";
import { DIST_EPSILON } from "geom/core";
import { line2Normal } from "geom/line2";
import {
  polyLine2AABB2,
  polyLine2ByTwoPoints,
  polyLine2Centroid,
  polyLine2Copy,
  polyLine2EdgeEval,
  polyLine2EdgeLength,
  polyLine2Length,
  polyLine2Reverse,
  polyLine2ReverseCopy,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { Rect2 } from "geom/rect2Types";
import { vec2Dist, vec2Dot, vec2Lerp } from "geom/vec2";
import { doorHasExteriorCasing, doorHasWrapping, doorIntersectsCutPlane } from "../doors/doorQuery";
import { planExtentsFromElevations, settingsClipFinish } from "../openings/openingUtils";
import { getActiveProfileDescriptorOrUndefined } from "../profileTools";
import { getWindowHeight } from "../window/windowHeightTools";
import {
  windowBackOpening3,
  windowFrontOpening3,
  windowHasCasing,
  windowHasWrapping,
  windowIntersectsCutPlane,
} from "../window/windowQuery";
import { solidWallLayerWithFallback } from "./wallLayers";

// Keep these local so cross-section helpers stay independent of doorUtils -> wall.ts.
function getDoorOpeningHeight(descriptor: DoorDescriptor): LevelInches {
  return levelInchesNew(descriptor.height - descriptor.zOffset);
}

function getDefaultDoorRoughOpeningAdjustment(door: GeneratedDoorPlacement): number {
  return door.descriptor.roughOpeningAdjustment ?? 0;
}

interface DoorGapsOptions {
  edge: PolyLine2;
  leftDoors: GeneratedDoorPlacement[];
  rightDoors: GeneratedDoorPlacement[];
  normalized?: boolean;
  includeExteriorCasing?: boolean;
  includeInteriorCasing?: boolean;
  profileSource?: ActiveProfileSource;
  includeRoughOpeningAdjustment?: boolean;
  getRoughOpeningAdjustment?: (door: GeneratedDoorPlacement) => number;
}

function doorGaps({
  edge,
  leftDoors,
  rightDoors,
  normalized = true,
  includeExteriorCasing,
  includeInteriorCasing = false,
  profileSource = undefined,
  includeRoughOpeningAdjustment = false,
  getRoughOpeningAdjustment = getDefaultDoorRoughOpeningAdjustment,
}: DoorGapsOptions): WallGap[] {
  const gaps: WallGap[] = [];

  const addDoorGap = (door: GeneratedDoorPlacement) => {
    const { opening, descriptor } = door;

    if (!opening) {
      return;
    }

    let { topOffset } = getDoorExteriorCasingOffsets({ door, includeExteriorCasing });

    if (includeInteriorCasing && profileSource) {
      ({ topOffset } = getDoorInteriorCasingOffsets(door, profileSource));
    }

    const roughOpeningAdjustment = includeRoughOpeningAdjustment ? getRoughOpeningAdjustment(door) : 0;

    let planOutline = wallOpeningPlanExtents(opening);
    if (includeExteriorCasing !== false && door.hasExteriorCasing) {
      planOutline = opening.planExtentsCasing;
    }

    // Each gap must carry only its own door so downstream consumers (e.g. wall-framing opening
    // handles) resolve the correct opening. Mirrors `windowGaps` passing `[window]` per gap.
    addWallGap(
      [door],
      gaps,
      edge,
      planOutline,
      descriptor.zOffset,
      getDoorOpeningHeight(door.descriptor) + topOffset,
      normalized,
      roughOpeningAdjustment,
      WallGapSource.DOOR,
    );
  };

  if (leftDoors) {
    for (let i = 0, li = leftDoors.length; i < li; i++) {
      addDoorGap(leftDoors[i]);
    }
  }

  if (rightDoors) {
    for (let i = 0, li = rightDoors.length; i < li; i++) {
      addDoorGap(rightDoors[i]);
    }
  }

  return gaps;
}

export function getDoorInteriorCasingOffsets(door: GeneratedDoorPlacement, profileSource: ActiveProfileSource) {
  let sideOffset = 0;
  let topOffset = 0;
  const interiorDoorCasing = door.interiorCasing ?? defaultInteriorDoorCasingSettings;
  let sideBoard: PolyLine2 | undefined = DOOR_UNIT_TRIM_CROSS_SECTION;
  let topBoard: PolyLine2 | undefined = DOOR_UNIT_TRIM_CROSS_SECTION;

  if (interiorDoorCasing.sideTrimType === ProfileExtrusionType.NONE) {
    sideBoard = undefined;
  } else if (interiorDoorCasing.sideTrimType !== undefined) {
    const sideDescriptor = getActiveProfileDescriptorOrUndefined(profileSource, interiorDoorCasing.sideTrimType);
    if (sideDescriptor?.profileType === ProfileExtrusionDescriptorType.BOARD) {
      const { width, height } = sideDescriptor as BoardDescriptor;
      sideBoard = polyLine2New([0, 0, 0, width, height, width, height, 0, 0, 0]);
    } else if (sideDescriptor?.profileType === ProfileExtrusionDescriptorType.CUSTOM) {
      const { polyline } = sideDescriptor as CustomDescriptor;
      sideBoard = polyLine2New(polyline);
    }
  }

  if (sideBoard) {
    const { minX: sideMinX, maxX: sideMaxX } = polyLine2AABB2(sideBoard);
    sideOffset = sideMaxX - sideMinX;
  }

  if (interiorDoorCasing.headTrimType === ProfileExtrusionType.NONE) {
    topBoard = undefined;
  } else if (interiorDoorCasing.headTrimType !== undefined) {
    const headDescriptor = getActiveProfileDescriptorOrUndefined(profileSource, interiorDoorCasing.headTrimType);
    if (headDescriptor?.profileType === ProfileExtrusionDescriptorType.BOARD) {
      const { width, height } = headDescriptor as BoardDescriptor;
      topBoard = polyLine2New([0, 0, 0, width, height, width, height, 0, 0, 0]);
    } else if (headDescriptor?.profileType === ProfileExtrusionDescriptorType.CUSTOM) {
      const { polyline } = headDescriptor as CustomDescriptor;
      topBoard = polyLine2New(polyline);
    }
  }

  if (topBoard) {
    const { minX: topMinX, maxX: topMaxX } = polyLine2AABB2(topBoard);
    topOffset = topMaxX - topMinX;
  }

  return { sideOffset, topOffset };
}

export function getDoorExteriorCasingOffsets({
  door,
  includeExteriorCasing,
  defaultDoorFrameWidth = 0,
}: {
  door: Partial<GeneratedDoorPlacement> &
    Pick<GeneratedDoorPlacement, "descriptor" | "exteriorCasing" | "streetOrientation">;
  includeExteriorCasing?: boolean;
  defaultDoorFrameWidth?: number;
}) {
  let sideOffset = defaultDoorFrameWidth;
  let topOffset = defaultDoorFrameWidth;
  const settings = (door.exteriorCasing ?? defaultExteriorDoorCasingSettings) as ExteriorDoorCasingSettings;

  if (!doorHasExteriorCasing(door, settings)) {
    return { sideOffset, topOffset };
  }

  if (doorHasWrapping(door)) {
    return { sideOffset: 0, topOffset: 0 };
  }

  const { sideCasingWidth } = settings;

  if (includeExteriorCasing || (includeExteriorCasing !== false && settingsClipFinish(settings))) {
    if (
      (includeExteriorCasing || settings.sideCasingFinishClipping !== FinishClipping.NO_CLIPPING) &&
      settings.sideCasingType !== SideCasingType.DISABLED &&
      sideCasingWidth !== undefined
    ) {
      sideOffset = sideCasingWidth;
    }

    if (
      (includeExteriorCasing || settings.headCasingFinishClipping !== FinishClipping.NO_CLIPPING) &&
      !isHeadCasingNone(settings) &&
      !isHeadCasingSlices(settings) &&
      !isHeadCasingArchedSlices(settings)
    ) {
      topOffset = settings.headCasingHeight;
    }
  }

  return { sideOffset, topOffset };
}

interface WallCutGapsOptions {
  edge: PolyLine2;
  leftWallCuts: WallCut[];
  rightWallCuts: WallCut[];
  normalized?: boolean;
}

function wallCutGaps({ edge, leftWallCuts, rightWallCuts, normalized = true }: WallCutGapsOptions): WallGap[] {
  const params: WallGap[] = [];

  const addWallCutGap = (wallCut: WallCut) => {
    const { opening } = wallCut;

    if (!opening) {
      return;
    }

    addWallGap([wallCut], params, edge, wallOpeningPlanExtents(opening), wallCut.bottom, wallCut.height, normalized);
  };

  if (leftWallCuts) {
    for (let i = 0, li = leftWallCuts.length; i < li; i++) {
      addWallCutGap(leftWallCuts[i]);
    }

    for (let i = 0, li = rightWallCuts.length; i < li; i++) {
      addWallCutGap(rightWallCuts[i]);
    }
  }

  return params;
}

interface WallMassDoorGapsOptions {
  includeExteriorCasing?: boolean;
  wallMass: Pick<WallMassBase, "leftEdge" | "rightEdge">;
  leftDoors: GeneratedDoorPlacement[];
  rightDoors: GeneratedDoorPlacement[];
  leftSide: boolean;
  normalized?: boolean;
  excludeUndimensionedOpenings?: boolean;
  /**
   * Pass both a cut plane and the room floor assemblies to leave the wall solid where a door
   * misses the plane, which is what plan cross sections want. 3D wall faces cut finishes at
   * arbitrary heights and pass no floor assemblies, so they keep carving out every door.
   */
  cutPlane?: LevelInches;
  roomFloorAssemblies?: Table<FloorAssembly>;
}

export function wallMassDoorGaps({
  includeExteriorCasing,
  wallMass,
  leftDoors,
  rightDoors,
  leftSide,
  normalized = true,
  excludeUndimensionedOpenings = false,
  cutPlane,
  roomFloorAssemblies,
}: WallMassDoorGapsOptions): WallGap[] {
  const { leftEdge, rightEdge } = wallMass;

  if (excludeUndimensionedOpenings) {
    leftDoors = leftDoors.filter((door) => !door.disableAutoDimensions);
    rightDoors = rightDoors.filter((door) => !door.disableAutoDimensions);
  }

  if (cutPlane !== undefined && roomFloorAssemblies !== undefined) {
    leftDoors = filterDoors(leftDoors, cutPlane, roomFloorAssemblies);
    rightDoors = filterDoors(rightDoors, cutPlane, roomFloorAssemblies);
  }

  return doorGaps({
    edge: leftSide ? leftEdge : rightEdge,
    includeExteriorCasing,
    leftDoors,
    rightDoors,
    normalized,
  });
}

export interface SolidWallGapsOptions {
  readonly wall: SolidWall;
  readonly isLeftSide: boolean;
  readonly normalized?: boolean;
  readonly mergeGaps?: boolean;
  readonly wallLayer?: WallLayerType;
  readonly excludeUndimensionedOpenings?: boolean;
  readonly includeWindows?: boolean;
  readonly includeDoors?: boolean;
}

export function solidWallGaps({
  wall,
  isLeftSide,
  normalized = true,
  mergeGaps = false,
  wallLayer = WallLayerType.SUBSTRATE,
  excludeUndimensionedOpenings = false,
  includeWindows = true,
  includeDoors = true,
}: SolidWallGapsOptions): StartSortedWallGaps {
  const wallGaps: WallGap[] = [];
  const wallMass = solidWallLayerWithFallback(wall, wallLayer);
  const { leftSide, rightSide } = wall;
  const { leftEdge, rightEdge } = wallMass;
  const edge = isLeftSide ? leftEdge : rightEdge;

  const includeExteriorCasing = wallLayer === WallLayerType.FINISH;

  if (includeWindows) {
    wallGaps.push(
      ...wallMassWindowGaps({
        wallMass,
        leftWindows: leftSide.windows,
        rightWindows: rightSide.windows,
        leftSide: isLeftSide,
        normalized,
        excludeUndimensionedOpenings,
        includeExteriorCasing,
      }),
    );
  }

  if (includeDoors) {
    wallGaps.push(
      ...wallMassDoorGaps({
        wallMass,
        includeExteriorCasing,
        leftDoors: leftSide.doors,
        rightDoors: rightSide.doors,
        leftSide: isLeftSide,
        normalized,
        excludeUndimensionedOpenings,
      }),
    );
  }

  const startSortedWallGaps: StartSortedWallGaps = startSortedWallGapsNew(edge, wallGaps);
  return mergeGaps ? mergeWallGaps(startSortedWallGaps) : startSortedWallGaps;
}

interface WallMassCutGapsOptions {
  wallMass: Pick<WallMassBase, "leftEdge" | "rightEdge">;
  leftWallCuts: WallCut[];
  rightWallCuts: WallCut[];
  leftSide: boolean;
  normalized?: boolean;
  cutPlane?: number;
}

function wallMassCutGaps({
  wallMass,
  leftWallCuts,
  rightWallCuts,
  leftSide,
  normalized = true,
  cutPlane,
}: WallMassCutGapsOptions): WallGap[] {
  const { leftEdge, rightEdge } = wallMass;

  const massLeftCuts = filterWallCuts(leftWallCuts, cutPlane);
  const massRightCuts = filterWallCuts(rightWallCuts, cutPlane);

  return wallCutGaps({
    edge: leftSide ? leftEdge : rightEdge,
    leftWallCuts: massLeftCuts,
    rightWallCuts: massRightCuts,
    normalized,
  });
}

function filterWallCuts(wallCuts: WallCut[], cutPlane = FLOOR_PLAN_CUT_PLANE) {
  return wallCuts.filter((x) => {
    const bottomOffset = x.bottom;
    const topOffset = x.bottom + x.height;
    return bottomOffset - DIST_EPSILON < cutPlane && topOffset + DIST_EPSILON > cutPlane;
  });
}

function addWallGap(
  opening: (GeneratedWindowPlacement | GeneratedDoorPlacement | WallCut)[],
  wallGaps: WallGap[],
  pl: PolyLine2,
  planOutline: PolyLine2,
  bottom: number,
  height: number,
  normalized: boolean = true,
  gapPadding: number = 0,
  gapSource?: WallGapSource,
  gapSourceId?: string,
) {
  const gap = makeWallGap(
    opening,
    pl,
    planOutline,
    bottom,
    height,
    normalized,
    gapPadding,
    false,
    gapSource,
    gapSourceId,
  );

  const edgeLen = polyLine2Length(pl);

  gap.start = Math.max(gap.start, 0);
  gap.end = Math.min(gap.end, edgeLen);

  if (gap.end > gap.start + DIST_EPSILON) {
    wallGaps.push(gap);
  }
}

export function makeWallGap(
  opening: (GeneratedWindowPlacement | GeneratedDoorPlacement | WallCut)[],
  edge: PolyLine2,
  planOutline: PolyLine2,
  bottom: number,
  height: number,
  normalized: boolean = true,
  gapPadding: number = 0,
  noMerge: boolean = false,
  gapSource?: WallGapSource,
  gapSourceId?: string,
): WallGap {
  const ax = edge[0];
  const ay = edge[1];
  const bx = edge[2];
  const by = edge[3];

  const len = vec2Dist(ax, ay, bx, by);
  const normalizedCasingWidth = gapPadding / len;

  const [startX, startY] = vec2Lerp(planOutline[2], planOutline[3], planOutline[4], planOutline[5], 0.5);
  const [centerX, centerY] = polyLine2Centroid(planOutline) ?? [];
  const [endX, endY] = vec2Lerp(planOutline[6], planOutline[7], planOutline[8], planOutline[9], 0.5);

  let startT = closestParamNormalizedLine2(ax, ay, bx, by, startX, startY);
  let centerT = closestParamNormalizedLine2(ax, ay, bx, by, centerX, centerY);
  let endT = closestParamNormalizedLine2(ax, ay, bx, by, endX, endY);

  if (vec2Dot(bx - ax, by - ay, endX - startX, endY - startY) < 0.0) {
    const tmp = startT;
    startT = endT;
    endT = tmp;
  }

  startT = Math.max(startT - normalizedCasingWidth, 0);
  endT = Math.min(endT + normalizedCasingWidth, 1);

  if (!normalized) {
    startT *= len;
    centerT *= len;
    endT *= len;
  }

  const hasCasing = gapPadding > DIST_EPSILON;

  return wallGapNew(
    opening,
    startT,
    centerT,
    endT,
    bottom,
    height,
    hasCasing,
    hasCasing,
    noMerge,
    gapSource,
    gapSourceId,
  );
}

interface WindowGapsOptions {
  edge: PolyLine2;
  leftWindows: GeneratedWindowPlacement[];
  rightWindows: GeneratedWindowPlacement[];
  includeExteriorCasing: boolean | undefined;
  includeApronCasing: boolean | undefined;
  normalized?: boolean;
  useWindowBottomZCalc?: boolean;
  cutPlane?: LevelInches;
}

function windowGaps({
  edge,
  leftWindows,
  rightWindows,
  includeExteriorCasing,
  cutPlane,
  includeApronCasing = includeExteriorCasing,
  normalized = true,
  useWindowBottomZCalc = false,
}: WindowGapsOptions): WallGap[] {
  const params: WallGap[] = [];

  const addWindowGap = (window: GeneratedWindowPlacement) => {
    const { topOffset, bottomOffset } = getWindowExteriorCasingOffsets({
      window,
      includeApronCasing,
      includeExteriorCasing,
    });

    const { opening, frontRoom, backRoom, headHeight = 0 } = window;
    if (!opening) {
      return;
    }

    const room = frontRoom ?? backRoom;
    const isWindowRoomClassGarage = room?.attributes.roomClass === RoomClass.GARAGE;
    let windowBottom = 0;

    if (isWindowRoomClassGarage && useWindowBottomZCalc) {
      windowBottom = wallOpeningBottomZ(opening) - room.level.floorElevation;
    } else {
      windowBottom = window.verticalOffset + headHeight - getWindowHeight(window.descriptor);
    }

    if (window.apronCasingClipsFinish) {
      windowBottom -= window.apronHeight;
    }

    let planOutline: Rect2 | undefined = undefined;
    const withCasing = includeExteriorCasing !== false && window.sideCasingClipsFinish && !windowHasWrapping(window);
    const [nx, ny] = line2Normal(edge[0], edge[1], edge[2], edge[3]);
    const frontOpening3 = windowFrontOpening3(window, withCasing);
    const backOpening3 = windowBackOpening3(window, withCasing);

    if (frontOpening3 && backOpening3) {
      planOutline = planExtentsFromElevations(
        [nx, ny],
        frontOpening3,
        backOpening3,
        cutPlane && frontOpening3
          ? {
              type: "PROFILE_AT_Z",
              z: levelInchesToWorld(cutPlane, ensureDefined(room).level.floorElevation),
            }
          : undefined,
      );
    }

    if (!planOutline || planOutline.length < 4) {
      return;
    }

    addWallGap(
      [window],
      params,
      edge,
      planOutline,
      windowBottom - bottomOffset,
      getWindowHeight(window.descriptor) + bottomOffset + topOffset,
      normalized,
      0,
      WallGapSource.WINDOW,
    );
  };

  if (leftWindows) {
    for (let i = 0, li = leftWindows.length; i < li; i++) {
      addWindowGap(leftWindows[i]);
    }
  }

  if (rightWindows) {
    for (let i = 0, li = rightWindows.length; i < li; i++) {
      addWindowGap(rightWindows[i]);
    }
  }

  return params;
}

export function getWindowInteriorCasingOffsets() {
  return {
    sideOffset: WINDOW_INTERIOR_FRAME_WIDTH,
    topOffset: WINDOW_INTERIOR_FRAME_WIDTH,
    bottomOffset: WINDOW_INTERIOR_FRAME_WIDTH,
  };
}

export function getWindowExteriorCasingOffsets({
  window,
  includeApronCasing,
  defaultWindowFrameWidth = 0,
  includeExteriorCasing,
}: {
  window: Partial<GeneratedWindowPlacement> &
    Pick<GeneratedWindowPlacement, "descriptor" | "exteriorCasing" | "streetOrientation">;
  includeApronCasing: boolean | undefined;
  defaultWindowFrameWidth?: number;
  includeExteriorCasing?: boolean;
}): { sideOffset: number; topOffset: number; bottomOffset: number } {
  let sideOffset = defaultWindowFrameWidth;
  let topOffset = defaultWindowFrameWidth;
  let bottomOffset = defaultWindowFrameWidth;

  const settings = (window.exteriorCasing ?? defaultExteriorWindowCasingSettings) as ExteriorWindowCasingSettings;
  const { apronCasingType, sideCasingWidth = 0, apronHeight = 0, stoolCasingThickness = 0 } = settings;

  if (!windowHasCasing(window, settings)) {
    return { sideOffset, topOffset, bottomOffset };
  }

  if (includeExteriorCasing || (includeExteriorCasing !== false && settingsClipFinish(settings))) {
    if (
      (includeExteriorCasing || settings.sideCasingFinishClipping !== FinishClipping.NO_CLIPPING) &&
      settings.sideCasingType !== SideCasingType.DISABLED
    ) {
      sideOffset = sideCasingWidth;
    }

    if (
      includeApronCasing &&
      (includeExteriorCasing || settings.apronCasingFinishClipping !== FinishClipping.NO_CLIPPING) &&
      apronCasingType !== ApronCasingType.DISABLED
    ) {
      bottomOffset = apronHeight;
    }

    if (
      (includeExteriorCasing || settings.stoolCasingFinishClipping !== FinishClipping.NO_CLIPPING) &&
      settings.stoolCasingType !== StoolCasingType.DISABLED
    ) {
      bottomOffset = stoolCasingThickness;
    }

    if (
      (includeExteriorCasing || settings.headCasingFinishClipping !== FinishClipping.NO_CLIPPING) &&
      settings.headCasingType !== HeadCasingType.NONE &&
      settings.headCasingType !== HeadCasingType.SLICES &&
      settings.headCasingType !== HeadCasingType.ARCHED_SLICES
    ) {
      topOffset = settings.headCasingHeight;
    }
  }

  return { sideOffset, topOffset, bottomOffset };
}

export function gableEndWindowGaps(
  edge: PolyLine2,
  edgeWindows: Table<GeneratedWindowPlacement>,
  normalized: boolean = false,
): WallGap[] {
  const params: WallGap[] = [];
  const edgeScale = normalized ? 1 / polyLine2EdgeLength(edge, 0) : 1;

  if (edgeWindows) {
    const scale = edgeScale;

    for (const windowId in edgeWindows) {
      const window = edgeWindows[windowId];
      const { opening, verticalOffset, descriptor } = window;
      if (!opening) {
        continue;
      }

      const winVertOffset = verticalOffset || 0;
      const hole = opening.front;
      params.push(
        wallGapNew(
          [window],
          hole.startParam * scale,
          hole.centerParam * scale,
          hole.endParam * scale,
          winVertOffset,
          getWindowHeight(descriptor),
          false,
          false,
          false,
          WallGapSource.WINDOW,
        ),
      );
    }
  }

  return params;
}

interface WallMassWindowGapsOptions {
  wallMass: Pick<WallMassBase, "leftEdge" | "rightEdge">;
  leftWindows: GeneratedWindowPlacement[];
  rightWindows: GeneratedWindowPlacement[];
  leftSide: boolean;
  normalized?: boolean;
  excludeUndimensionedOpenings?: boolean;
  includeExteriorCasing?: boolean;
  includeApronCasing?: boolean;
  cutPlane?: LevelInches;
  useWindowBottomZCalc?: boolean;
  roomFloorAssemblies?: Table<FloorAssembly>;
}

export function wallMassWindowGaps({
  wallMass,
  leftWindows,
  rightWindows,
  leftSide,
  normalized = true,
  excludeUndimensionedOpenings = false,
  includeExteriorCasing,
  includeApronCasing,
  cutPlane,
  useWindowBottomZCalc,
  roomFloorAssemblies,
}: WallMassWindowGapsOptions): WallGap[] {
  const { leftEdge, rightEdge } = wallMass;

  if (excludeUndimensionedOpenings) {
    leftWindows = leftWindows.filter((window) => !window.disableAutoDimensions);
    rightWindows = rightWindows.filter((window) => !window.disableAutoDimensions);
  }

  leftWindows = filterWindows(leftWindows, cutPlane, roomFloorAssemblies);
  rightWindows = filterWindows(rightWindows, cutPlane, roomFloorAssemblies);

  return windowGaps({
    edge: leftSide ? leftEdge : rightEdge,
    leftWindows,
    rightWindows,
    normalized,
    includeExteriorCasing,
    includeApronCasing,
    useWindowBottomZCalc,
    cutPlane,
  });
}

function filterWindows(
  windows: GeneratedWindowPlacement[],
  cutPlane?: LevelInches,
  roomFloorAssemblies?: Table<FloorAssembly>,
) {
  return windows.filter((x) => {
    return windowIntersectsCutPlane(x, cutPlane, roomFloorAssemblies);
  });
}

function filterDoors(
  doors: GeneratedDoorPlacement[],
  cutPlane: LevelInches,
  roomFloorAssemblies: Table<FloorAssembly>,
) {
  return doors.filter((x) => {
    return doorIntersectsCutPlane(x, cutPlane, roomFloorAssemblies);
  });
}

export function openTopRect(
  botLeftX: number,
  botLeftY: number,
  topLeftX: number,
  topLeftY: number,
  botRightX: number,
  botRightY: number,
  topRightX: number,
  topRightY: number,
) {
  const outline = polyLine2New(8);
  outline[0] = topLeftX;
  outline[1] = topLeftY;
  outline[2] = botLeftX;
  outline[3] = botLeftY;
  outline[4] = botRightX;
  outline[5] = botRightY;
  outline[6] = topRightX;
  outline[7] = topRightY;
  return outline;
}

export function openBottomRect(
  botLeftX: number,
  botLeftY: number,
  topLeftX: number,
  topLeftY: number,
  botRightX: number,
  botRightY: number,
  topRightX: number,
  topRightY: number,
) {
  const outline = polyLine2New(8);
  outline[0] = botRightX;
  outline[1] = botRightY;
  outline[2] = topRightX;
  outline[3] = topRightY;
  outline[4] = topLeftX;
  outline[5] = topLeftY;
  outline[6] = botLeftX;
  outline[7] = botLeftY;
  return outline;
}

export function rect(
  botLeftX: number,
  botLeftY: number,
  topLeftX: number,
  topLeftY: number,
  botRightX: number,
  botRightY: number,
  topRightX: number,
  topRightY: number,
) {
  const outline = polyLine2New(10);
  outline[0] = botLeftX;
  outline[1] = botLeftY;
  outline[2] = botRightX;
  outline[3] = botRightY;
  outline[4] = topRightX;
  outline[5] = topRightY;
  outline[6] = topLeftX;
  outline[7] = topLeftY;
  outline[8] = botLeftX;
  outline[9] = botLeftY;
  return outline;
}

interface BuildSolidWallCrossSectionOutlinesOptions {
  leftSide: SolidWallSide;
  rightSide: SolidWallSide;
  crossSectionMass: Pick<WallMassBase, "leftEdge" | "rightEdge">;
  outlineBuilder: (i: number, leftPts: number[], rightPts: number[]) => unknown;
  includeDoors?: boolean;
  includeWindows?: boolean;
  includeWallCuts?: boolean;
  cutPlane?: LevelInches;
  includeExteriorCasing?: boolean;
  roomFloorAssemblies?: Table<FloorAssembly>;
}

export function buildSolidWallCrossSectionOutlines({
  leftSide,
  rightSide,
  crossSectionMass,
  outlineBuilder,
  includeDoors = true,
  includeWindows = true,
  includeWallCuts = true,
  includeExteriorCasing,
  cutPlane,
  roomFloorAssemblies,
}: BuildSolidWallCrossSectionOutlinesOptions) {
  const { leftEdge, rightEdge } = crossSectionMass;
  const leftGaps: WallGap[] = [];
  const rightGaps: WallGap[] = [];

  const windowsWithOpenings = [...leftSide.windows, ...rightSide.windows].filter((window) => !!window.opening);
  if (includeWindows && windowsWithOpenings.length > 0) {
    leftGaps.push(
      ...wallMassWindowGaps({
        wallMass: crossSectionMass,
        leftWindows: leftSide.windows,
        rightWindows: rightSide.windows,
        leftSide: true,
        normalized: false,
        cutPlane,
        includeExteriorCasing,
        roomFloorAssemblies,
      }),
    );
    rightGaps.push(
      ...wallMassWindowGaps({
        wallMass: crossSectionMass,
        leftWindows: leftSide.windows,
        rightWindows: rightSide.windows,
        leftSide: false,
        normalized: false,
        cutPlane,
        includeExteriorCasing,
        roomFloorAssemblies,
      }),
    );
  }

  const doorsWithOpenings = [...leftSide.doors, ...rightSide.doors].filter((door) => !!door.opening);
  if (includeDoors && doorsWithOpenings.length > 0) {
    leftGaps.push(
      ...wallMassDoorGaps({
        wallMass: crossSectionMass,
        leftDoors: leftSide.doors,
        rightDoors: rightSide.doors,
        includeExteriorCasing,
        leftSide: true,
        normalized: false,
        cutPlane,
        roomFloorAssemblies,
      }),
    );

    rightGaps.push(
      ...wallMassDoorGaps({
        wallMass: crossSectionMass,
        leftDoors: leftSide.doors,
        rightDoors: rightSide.doors,
        includeExteriorCasing,
        leftSide: false,
        normalized: false,
        cutPlane,
        roomFloorAssemblies,
      }),
    );
  }

  const wallCutsWithOpenings = [...leftSide.wallCuts, ...rightSide.wallCuts].filter((wallCut) => !!wallCut.opening);
  if (includeWallCuts && wallCutsWithOpenings.length > 0) {
    leftGaps.push(
      ...wallMassCutGaps({
        wallMass: crossSectionMass,
        leftWallCuts: leftSide.wallCuts,
        rightWallCuts: rightSide.wallCuts,
        leftSide: true,
        normalized: false,
        cutPlane,
      }),
    );
    rightGaps.push(
      ...wallMassCutGaps({
        wallMass: crossSectionMass,
        leftWallCuts: leftSide.wallCuts,
        rightWallCuts: rightSide.wallCuts,
        leftSide: false,
        normalized: false,
        cutPlane,
      }),
    );
  }

  if (leftGaps.length === 0 && rightGaps.length === 0) {
    return { leftEdge, rightEdge, leftGaps, rightGaps };
  }

  const sortedLeftGaps = startSortedWallGapsNew(leftEdge, leftGaps);
  const sortedRightGaps = startSortedWallGapsNew(rightEdge, rightGaps);
  const mergedLeftGaps = mergeWallGaps(sortedLeftGaps);
  const mergedRightGaps = mergeWallGaps(sortedRightGaps);

  const leftPts = [leftEdge[leftEdge.length - 2], leftEdge[leftEdge.length - 1]];
  const rightPts = [rightEdge[0], rightEdge[1]];

  addCrossSectionPoints(leftPts, rightPts, leftEdge, rightEdge, mergedLeftGaps.gaps, mergedRightGaps.gaps);

  leftPts.push(leftEdge[0], leftEdge[1]);
  rightPts.push(rightEdge[rightEdge.length - 2], rightEdge[rightEdge.length - 1]);

  for (let i = 0, li = leftPts.length; i < li; i += 4) {
    outlineBuilder(i, leftPts, rightPts);
  }

  return { leftEdge, rightEdge, leftGaps, rightGaps };
}

export function solidWallCrossSectionEdges({
  leftSide,
  rightSide,
  includeDoors = true,
  includeWindows = true,
  includeWallCuts = true,
  includeExteriorCasing,
  wallMass,
  maintainEdgeOrientation = true,
  cutPlane,
}: {
  leftSide: SolidWallSide;
  rightSide: SolidWallSide;
  wallMass: Pick<WallMassBase, "leftEdge" | "rightEdge">;
  includeDoors?: boolean;
  includeWindows?: boolean;
  includeWallCuts?: boolean;
  includeExteriorCasing?: boolean;
  maintainEdgeOrientation?: boolean;
  cutPlane?: LevelInches;
}) {
  const leftOutlines: PolyLine2[] = [];
  const rightOutlines: PolyLine2[] = [];

  const { leftEdge, rightEdge, leftGaps, rightGaps } = buildSolidWallCrossSectionOutlines({
    leftSide,
    rightSide,
    crossSectionMass: wallMass,
    outlineBuilder: (i: number, leftPts: number[], rightPts: number[]) => {
      const [botLeftX, botLeftY, topLeftX, topLeftY] = leftPts.slice(i, i + 4);
      const [botRightX, botRightY, topRightX, topRightY] = rightPts.slice(i, i + 4);
      leftOutlines.push(polyLine2ByTwoPoints(botLeftX, botLeftY, topLeftX, topLeftY));
      rightOutlines.push(polyLine2ByTwoPoints(botRightX, botRightY, topRightX, topRightY));
    },
    includeDoors,
    includeWindows,
    includeWallCuts,
    cutPlane,
    includeExteriorCasing,
  });

  if (leftGaps.length === 0 && rightGaps.length === 0) {
    return { leftOutlines: [leftEdge], rightOutlines: [rightEdge] };
  }

  if (maintainEdgeOrientation) {
    leftOutlines.forEach((outline) => polyLine2Reverse(outline));
    leftOutlines.reverse();
  }

  return { leftOutlines, rightOutlines };
}

export function wallMassBaseCrossSection(wall: WallMassBase, closedPolyLines: boolean = false): PolyLine2[] {
  const { leftEdge, rightEdge } = wall;
  const topRect = closedPolyLines ? rect : openTopRect;
  const bottomRect = closedPolyLines ? rect : openBottomRect;

  const botLeftX = leftEdge[2];
  const botLeftY = leftEdge[3];
  const topLeftX = leftEdge[0];
  const topLeftY = leftEdge[1];
  const botRightX = rightEdge[0];
  const botRightY = rightEdge[1];
  const topRightX = rightEdge[2];
  const topRightY = rightEdge[3];

  if (wall.start.isFreeEnd) {
    if (wall.end.isFreeEnd) {
      return [rect(botLeftX, botLeftY, topLeftX, topLeftY, botRightX, botRightY, topRightX, topRightY)];
    }

    return [topRect(botLeftX, botLeftY, topLeftX, topLeftY, botRightX, botRightY, topRightX, topRightY)];
  }

  if (wall.end.isFreeEnd) {
    return [bottomRect(botLeftX, botLeftY, topLeftX, topLeftY, botRightX, botRightY, topRightX, topRightY)];
  }

  if (closedPolyLines) {
    const firstHalf: number[] = [];
    const secondHalf: number[] = [];

    if (wall.start.offsetDifferenceOutline) {
      firstHalf.push(
        wall.start.offsetDifferenceOutline[0],
        wall.start.offsetDifferenceOutline[1],
        wall.start.offsetDifferenceOutline[2],
        wall.start.offsetDifferenceOutline[3],
        botRightX,
        botRightY,
        topRightX,
        topRightY,
      );
      secondHalf.push(topLeftX, topLeftY, wall.start.offsetDifferenceOutline[0], wall.start.offsetDifferenceOutline[1]);
    } else {
      firstHalf.push(botLeftX, botLeftY, botRightX, botRightY, topRightX, topRightY);
      secondHalf.push(topLeftX, topLeftY, botLeftX, botLeftY);
    }

    if (wall.end.offsetDifferenceOutline) {
      return [
        polyLine2New([
          ...firstHalf,
          wall.end.offsetDifferenceOutline[0],
          wall.end.offsetDifferenceOutline[1],
          wall.end.offsetDifferenceOutline[2],
          wall.end.offsetDifferenceOutline[3],
          ...secondHalf,
        ]),
      ];
    }

    return [polyLine2New([...firstHalf, topLeftX, topLeftY, ...secondHalf])];
  }

  const edgePolys: PolyLine2[] = [];
  if (wall.start.offsetDifferenceOutline) {
    edgePolys.push(wall.start.offsetDifferenceOutline as unknown as PolyLine2);
  }

  edgePolys.push(polyLine2ReverseCopy(leftEdge));

  if (wall.end.offsetDifferenceOutline) {
    edgePolys.push(wall.end.offsetDifferenceOutline as unknown as PolyLine2);
  }

  edgePolys.push(polyLine2Copy(rightEdge));
  return edgePolys;
}

function addCrossSectionPoints(
  leftPts: number[],
  rightPts: number[],
  leftEdge: PolyLine2,
  rightEdge: PolyLine2,
  mergedLeftGaps: WallGap[],
  mergedRightGaps: WallGap[],
) {
  for (let i = 0, li = Math.min(mergedLeftGaps.length, mergedRightGaps.length); i < li; i++) {
    let [leftX, leftY] = polyLine2EdgeEval(leftEdge, 0, mergedLeftGaps[mergedLeftGaps.length - i - 1].end);
    let [rightX, rightY] = polyLine2EdgeEval(rightEdge, 0, mergedRightGaps[i].start);

    leftPts.push(leftX, leftY);
    rightPts.push(rightX, rightY);

    [leftX, leftY] = polyLine2EdgeEval(leftEdge, 0, mergedLeftGaps[mergedLeftGaps.length - i - 1].start);
    [rightX, rightY] = polyLine2EdgeEval(rightEdge, 0, mergedRightGaps[i].end);

    leftPts.push(leftX, leftY);
    rightPts.push(rightX, rightY);
  }
}

function buildOutlines(leftPts: number[], rightPts: number[]) {
  const outlines: PolyLine2[] = [];

  for (let i = 0, li = leftPts.length; i < li; i += 4) {
    const [botLeftX, botLeftY, topLeftX, topLeftY] = leftPts.slice(i, i + 4);
    const [botRightX, botRightY, topRightX, topRightY] = rightPts.slice(i, i + 4);
    outlines.push(rect(botLeftX, botLeftY, topLeftX, topLeftY, botRightX, botRightY, topRightX, topRightY));
  }

  return outlines;
}

export function makeCrossSectionFromWallGaps(
  leftGaps: StartSortedWallGaps,
  rightGaps: StartSortedWallGaps,
): PolyLine2[] {
  const { edge: leftEdge, gaps: mergedLeftGaps } = mergeWallGaps(leftGaps);
  const { edge: rightEdge, gaps: mergedRightGaps } = mergeWallGaps(rightGaps);

  const leftPts = [leftEdge[leftEdge.length - 2], leftEdge[leftEdge.length - 1]];
  const rightPts = [rightEdge[0], rightEdge[1]];
  addCrossSectionPoints(leftPts, rightPts, leftEdge, rightEdge, mergedLeftGaps, mergedRightGaps);

  leftPts.push(leftEdge[0], leftEdge[1]);
  rightPts.push(rightEdge[rightEdge.length - 2], rightEdge[rightEdge.length - 1]);

  return buildOutlines(leftPts, rightPts);
}

export function makeWallGapCrossSections(leftGaps: StartSortedWallGaps, rightGaps: StartSortedWallGaps): PolyLine2[] {
  const { edge: leftEdge, gaps: mergedLeftGaps } = mergeWallGaps(leftGaps);
  const { edge: rightEdge, gaps: mergedRightGaps } = mergeWallGaps(rightGaps);

  const leftPts: number[] = [];
  const rightPts: number[] = [];

  addCrossSectionPoints(leftPts, rightPts, leftEdge, rightEdge, mergedLeftGaps, mergedRightGaps);
  return buildOutlines(leftPts, rightPts);
}

export function mergeWallGaps(srcGaps: StartSortedWallGaps): StartSortedWallGaps {
  const mergedGaps: StartSortedWallGaps = { edge: srcGaps.edge, gaps: [] };
  let currGap: WallGap | null = null;
  let currTop = 0;

  for (let i = 0, li = srcGaps.gaps.length; i < li; i++) {
    const {
      openings: srcOpenings,
      start,
      end,
      center,
      bottom,
      height,
      hasStartCasing,
      hasEndCasing,
      noMerge,
      source,
      sourceId,
    } = srcGaps.gaps[i];

    if (currGap) {
      if (start >= currGap.start - DIST_EPSILON && start <= currGap.end + DIST_EPSILON && !noMerge) {
        if (Math.abs(start - currGap.start) <= DIST_EPSILON) {
          currGap.hasStartCasing = currGap.hasStartCasing || hasStartCasing;
        }

        if (Math.abs(end - currGap.end) <= DIST_EPSILON) {
          currGap.hasEndCasing = currGap.hasEndCasing || hasEndCasing;
        } else if (end > currGap.end) {
          currGap.hasEndCasing = hasEndCasing;
        }

        currTop = Math.max(currTop, bottom + height);
        currGap.end = Math.max(currGap.end, end);
        currGap.center = (currGap.start + currGap.end) / 2;
        currGap.bottom = Math.min(currGap.bottom, bottom);
        currGap.height = Math.max(currGap.height, currTop - currGap.bottom);
        currGap.openings = [...currGap.openings, ...srcOpenings];

        if (currGap.source !== source) {
          currGap.source = WallGapSource.MULTIPLE;
        }
      } else {
        mergedGaps.gaps.push(currGap);
        currGap = null;
        currTop = 0;
      }
    }

    if (!currGap) {
      currGap = wallGapNew(
        srcOpenings,
        start,
        center,
        end,
        bottom,
        height,
        hasStartCasing,
        hasEndCasing,
        noMerge,
        source,
        sourceId,
      );
      currTop = bottom + height;
    }
  }

  if (currGap) {
    mergedGaps.gaps.push(currGap);
  }

  return mergedGaps;
}

interface WallGapsOptions {
  leftEdge: PolyLine2;
  rightEdge: PolyLine2;
  leftSide: SolidWallSide;
  rightSide: SolidWallSide;
  isLeftSide: boolean;
  includeExteriorCasing?: boolean;
  includeInteriorCasing?: boolean;
  profileSource?: ActiveProfileSource;
  includeApronCasing?: boolean;
  includeRoughOpeningAdjustment?: boolean;
  getDoorRoughOpeningAdjustment?: (door: GeneratedDoorPlacement) => number;
  mergeGaps?: boolean;
  cutPlane?: LevelInches;
  useWindowBottomZCalc?: boolean;
}

export function getWallGaps({
  leftEdge,
  rightEdge,
  leftSide,
  rightSide,
  isLeftSide,
  includeExteriorCasing,
  includeApronCasing = includeExteriorCasing,
  includeInteriorCasing = false,
  profileSource = undefined,
  includeRoughOpeningAdjustment = false,
  cutPlane,
  getDoorRoughOpeningAdjustment,
  mergeGaps = false,
  useWindowBottomZCalc = false,
}: WallGapsOptions): WallGap[] {
  const edge = isLeftSide ? leftEdge : rightEdge;
  let tmpGaps = wallMassWindowGaps({
    wallMass: { leftEdge, rightEdge },
    leftSide: isLeftSide,
    leftWindows: leftSide.windows,
    rightWindows: rightSide.windows,
    includeExteriorCasing,
    includeApronCasing,
    normalized: false,
    cutPlane,
    useWindowBottomZCalc,
  });

  tmpGaps.push(
    ...doorGaps({
      edge,
      leftDoors: leftSide.doors,
      rightDoors: rightSide.doors,
      includeExteriorCasing,
      includeInteriorCasing,
      profileSource,
      includeRoughOpeningAdjustment,
      getRoughOpeningAdjustment: getDoorRoughOpeningAdjustment,
      normalized: false,
    }),
  );

  tmpGaps.push(
    ...wallCutGaps({
      edge,
      leftWallCuts: leftSide.wallCuts,
      rightWallCuts: rightSide.wallCuts,
      normalized: false,
    }),
  );

  const edgeLength = polyLine2Length(edge);
  tmpGaps = tmpGaps.filter((gap) => gap.start < edgeLength - DIST_EPSILON && gap.end > DIST_EPSILON);

  return mergeGaps ? mergeWallGaps(startSortedWallGapsNew(edge, tmpGaps)).gaps : tmpGaps;
}
